/**
 * PRD.330 Phase 2 — Column filter popover (draft → Apply).
 *
 * Portal-rendered and anchored to the header's filter button, positioned by
 * `computePopoverPosition` (viewport clamp + flip). Interaction contract:
 *   - opening seeds the draft from the APPLIED filter (reducer `openFilter`);
 *   - edits change only the draft;
 *   - Apply (button or Enter) validates + commits; invalid drafts show inline
 *     feedback and nothing is applied;
 *   - Escape / Cancel / outside mousedown close WITHOUT committing;
 *   - Clear removes only this column's applied filter.
 * Keyboard handling is scoped to the popover element — no window/document
 * key listeners, so typing in inputs and checkboxes behaves normally.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@helios/ui';
import { filterFacetOptions } from '@helios/domain';
import { computePopoverPosition, type PopoverPlacement } from './popover-position';
import type { ReportFilterDraft } from './report-state-logic';
import type { ReportFacetListItem } from './report-view-logic';

const POPOVER_WIDTH = 280;

const inputStyle: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontFamily: 'var(--font-family)',
  boxSizing: 'border-box',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-1)',
};

const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '4px 0',
  minHeight: 28,
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  cursor: 'pointer',
};

export interface ReportFilterPopoverProps {
  anchor: HTMLElement;
  columnLabel: string;
  draft: ReportFilterDraft;
  error: string | null;
  /** Facet options (values filters only): supplied facet data + stale selections. */
  facetItems: ReadonlyArray<ReportFacetListItem>;
  /** Facet options for this column are still loading. */
  optionsLoading?: boolean;
  sortable: boolean;
  onEditDraft: (draft: ReportFilterDraft) => void;
  onSelectVisible: (values: ReadonlyArray<string>) => void;
  onClearVisible: (values: ReadonlyArray<string>) => void;
  onApply: () => void;
  onCancel: () => void;
  onClear: () => void;
  onSort: (direction: 'asc' | 'desc') => void;
}

export function ReportFilterPopover(props: ReportFilterPopoverProps) {
  const { anchor, columnLabel, draft, error, onApply, onCancel, onClear } = props;
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPlacement | null>(null);
  const titleId = useId();
  const errorId = useId();

  // Close without committing on outside mousedown (not a keyboard handler).
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || anchor.contains(target)) return;
      onCancel();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [anchor, onCancel]);

  // Anchor + collision handling; re-measure on scroll (capture) and resize.
  useLayoutEffect(() => {
    const compute = () => {
      const rect = anchor.getBoundingClientRect();
      const el = popoverRef.current;
      setPosition(
        computePopoverPosition(
          rect,
          { width: el?.offsetWidth ?? POPOVER_WIDTH, height: el?.scrollHeight ?? 320 },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [anchor, draft.kind]);

  // Focus the first field when opened.
  useEffect(() => {
    const root = popoverRef.current;
    const first =
      root?.querySelector<HTMLElement>('input') ?? root?.querySelector<HTMLElement>('button');
    first?.focus();
  }, []);

  const closeAndRefocus = (fn: () => void) => {
    fn();
    anchor.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      closeAndRefocus(onCancel);
    }
  };

  const optionsLoading = draft.kind === 'values' && props.optionsLoading === true;
  const applyDisabled = draft.kind === 'values' && (optionsLoading || draft.selected.length === 0);

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: POPOVER_WIDTH,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: position?.maxHeight,
        overflowY: 'auto',
        zIndex: 1000,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg, 0 10px 30px rgba(15, 23, 42, 0.15))',
        padding: 'var(--sp-3)',
        boxSizing: 'border-box',
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!applyDisabled) {
            onApply();
          }
        }}
      >
        <div
          id={titleId}
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            marginBottom: 'var(--sp-2)',
          }}
        >
          Filter {columnLabel}
        </div>

        {props.sortable && (
          <div style={{ display: 'flex', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
            <Button type="button" size="sm" variant="outline" onClick={() => props.onSort('asc')}>
              Sort ascending
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => props.onSort('desc')}>
              Sort descending
            </Button>
          </div>
        )}

        {draft.kind === 'text' && (
          <TextPanel
            draft={draft}
            errorId={errorId}
            hasError={!!error}
            onEdit={props.onEditDraft}
          />
        )}
        {draft.kind === 'values' && (
          <ValuesPanel
            draft={draft}
            items={props.facetItems}
            loading={optionsLoading}
            errorId={errorId}
            hasError={!!error}
            onEdit={props.onEditDraft}
            onSelectVisible={props.onSelectVisible}
            onClearVisible={props.onClearVisible}
          />
        )}
        {draft.kind === 'range' && (
          <RangePanel
            draft={draft}
            errorId={errorId}
            hasError={!!error}
            onEdit={props.onEditDraft}
          />
        )}

        {(error || (applyDisabled && !optionsLoading)) && (
          <div
            id={errorId}
            role="alert"
            style={{
              marginTop: 'var(--sp-2)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-danger, #dc2626)',
            }}
          >
            {error ?? 'Select at least one value, or use Clear.'}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--sp-2)',
            marginTop: 'var(--sp-3)',
          }}
        >
          <Button type="button" size="sm" variant="ghost" onClick={() => closeAndRefocus(onClear)}>
            Clear
          </Button>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => closeAndRefocus(onCancel)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="solid" disabled={applyDisabled}>
              Apply
            </Button>
          </div>
        </div>
      </form>
    </div>,
    document.body,
  );
}

// ─── Panels ───

function TextPanel({
  draft,
  errorId,
  hasError,
  onEdit,
}: {
  draft: Extract<ReportFilterDraft, { kind: 'text' }>;
  errorId: string;
  hasError: boolean;
  onEdit: (d: ReportFilterDraft) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} style={fieldLabelStyle}>
        Contains
      </label>
      <input
        id={id}
        type="text"
        value={draft.value}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        onChange={(e) => onEdit({ kind: 'text', value: e.target.value })}
        style={inputStyle}
      />
    </div>
  );
}

function ValuesPanel({
  draft,
  items,
  loading,
  errorId,
  hasError,
  onEdit,
  onSelectVisible,
  onClearVisible,
}: {
  draft: Extract<ReportFilterDraft, { kind: 'values' }>;
  items: ReadonlyArray<ReportFacetListItem>;
  loading: boolean;
  errorId: string;
  hasError: boolean;
  onEdit: (d: ReportFilterDraft) => void;
  onSelectVisible: (values: ReadonlyArray<string>) => void;
  onClearVisible: (values: ReadonlyArray<string>) => void;
}) {
  const searchId = useId();
  // Option search filters the OPTIONS shown here — never report rows.
  const visible = filterFacetOptions(items, draft.optionSearch);
  const visibleValues = visible.map((o) => o.value);
  const searching = draft.optionSearch.trim() !== '';
  const selected = new Set(draft.selected);

  const toggle = (value: string) => {
    const next = selected.has(value)
      ? draft.selected.filter((v) => v !== value)
      : [...draft.selected, value];
    onEdit({ ...draft, selected: next });
  };

  return (
    <div>
      <label htmlFor={searchId} style={fieldLabelStyle}>
        Search options
      </label>
      <input
        id={searchId}
        type="search"
        value={draft.optionSearch}
        onChange={(e) => onEdit({ ...draft, optionSearch: e.target.value })}
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 'var(--sp-3)', margin: 'var(--sp-2) 0 var(--sp-1)' }}>
        <button
          type="button"
          style={linkButtonStyle}
          disabled={loading || visibleValues.length === 0}
          onClick={() => onSelectVisible(visibleValues)}
        >
          {searching ? 'Select all matches' : 'Select all'}
        </button>
        <button
          type="button"
          style={linkButtonStyle}
          disabled={loading || visibleValues.length === 0}
          onClick={() => onClearVisible(visibleValues)}
        >
          {searching ? 'Clear all matches' : 'Clear all'}
        </button>
      </div>
      <fieldset
        aria-describedby={hasError ? errorId : undefined}
        style={{ border: 'none', margin: 0, padding: 0, maxHeight: 220, overflowY: 'auto' }}
      >
        <legend
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          Values
        </legend>
        {loading && (
          <div
            role="status"
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              padding: 'var(--sp-2) 0',
            }}
          >
            Loading options…
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              padding: 'var(--sp-2) 0',
            }}
          >
            No matching options.
          </div>
        )}
        {visible.map((option) => (
          <label
            key={option.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              minHeight: 32,
              fontSize: 'var(--font-size-sm)',
              color: option.stale ? 'var(--text-muted)' : 'var(--text)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(option.value)}
              onChange={() => toggle(option.value)}
              style={{ width: 16, height: 16, accentColor: 'var(--color-primary)' }}
            />
            <span style={{ flex: 1 }}>
              {option.label}
              {option.stale && <span> (no matches)</span>}
            </span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {option.count}
            </span>
          </label>
        ))}
      </fieldset>
    </div>
  );
}

function RangePanel({
  draft,
  errorId,
  hasError,
  onEdit,
}: {
  draft: Extract<ReportFilterDraft, { kind: 'range' }>;
  errorId: string;
  hasError: boolean;
  onEdit: (d: ReportFilterDraft) => void;
}) {
  const minId = useId();
  const maxId = useId();
  const field = (id: string, label: string, value: string, key: 'min' | 'max'): ReactNode => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label htmlFor={id} style={fieldLabelStyle}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={value}
        placeholder="Any"
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        onChange={(e) => onEdit({ ...draft, [key]: e.target.value })}
        style={inputStyle}
      />
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        {field(minId, 'Minimum (≥)', draft.min, 'min')}
        {field(maxId, 'Maximum (≤)', draft.max, 'max')}
      </div>
      <div
        style={{
          marginTop: 'var(--sp-1)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
        }}
      >
        Both bounds are inclusive. Leave one empty for an open range.
      </div>
    </div>
  );
}
